import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Upload,
  FileUp,
  AlertCircle,
  CheckCircle2,
  FileText,
  Loader2,
  Download,
} from 'lucide-react'
import { UnresolvedReferencesPanel } from './UnresolvedReferencesPanel'
import {
  downloadRelationshipTemplate,
  exportUnresolvedReferences,
} from '../../services/relationship-import'
import type {
  RelationshipImportValidationResult,
  RelationshipImportExecuteResult,
  DuplicateRelationship,
  RelationshipPreviewRow,
} from '../../types/relationship-import'
import { MAX_ERROR_ROWS, MAX_DUPLICATE_ROWS, MAX_PREVIEW_ROWS } from './RelationshipImportModal.helpers'

export interface FileDropZoneProps {
  caseId: string
  dragActive: boolean
  onDrag: (event: React.DragEvent) => void
  onDrop: (event: React.DragEvent) => void
  onBrowse: () => void
  fileInputRef: React.RefObject<HTMLInputElement>
  onInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void
}

export function RelationshipFileDropZone({
  caseId,
  dragActive,
  onDrag,
  onDrop,
  onBrowse,
  fileInputRef,
  onInputChange,
}: FileDropZoneProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const dropZoneClass = dragActive
    ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/20'
    : 'border-slate-300 dark:border-slate-600 hover:border-slate-400'
  const iconClass = dragActive ? 'text-blue-500' : 'text-slate-400 dark:text-slate-500'
  return (
    <div className="space-y-4">
      <div
        onDragEnter={onDrag}
        onDragLeave={onDrag}
        onDragOver={onDrag}
        onDrop={onDrop}
        className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-12 transition-colors ${dropZoneClass}`}
      >
        <FileUp className={`h-12 w-12 ${iconClass}`} />
        <p className="mt-4 text-center text-slate-600 dark:text-slate-400">
          {t('infrastructure.importRelationship.dragDropText')}{' '}
          <button onClick={onBrowse} className="text-blue-600 hover:underline dark:text-blue-400">
            {t('infrastructure.importRelationship.browse')}
          </button>
        </p>
        <p className="mt-2 text-sm text-slate-500">
          {t('infrastructure.importRelationship.supportsFormats')}
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={onInputChange}
          className="hidden"
        />
      </div>
      <div className="flex justify-end">
        <button
          onClick={() => void downloadRelationshipTemplate(caseId)}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-600 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
        >
          <Download className="h-4 w-4" />
          {t('infrastructure.importRelationship.downloadTemplate')}
        </button>
      </div>
    </div>
  )
}

export function ValidationSummaryCard({
  result,
}: {
  result: RelationshipImportValidationResult
}): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="flex items-start gap-4 rounded-lg border border-slate-200 p-4 dark:border-slate-700">
      <div className="flex-shrink-0">
        {result.valid ? (
          <CheckCircle2 className="h-6 w-6 text-green-500" />
        ) : (
          <AlertCircle className="h-6 w-6 text-red-500" />
        )}
      </div>
      <div>
        <h3 className="font-medium text-slate-900 dark:text-white">
          {result.valid
            ? t('infrastructure.importRelationship.fileValid')
            : t('infrastructure.importRelationship.validationFailed')}
        </h3>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          {t('infrastructure.importRelationship.recordsSummary', {
            fileName: result.fileName,
            total: result.totalRecords,
            valid: result.validRecords,
          })}
        </p>
      </div>
    </div>
  )
}

export function ValidationErrorsList({
  errors,
}: {
  errors: RelationshipImportValidationResult['errors']
}): React.ReactElement {
  const { t } = useTranslation('cases')
  const visible = errors.slice(0, MAX_ERROR_ROWS)
  const hidden = errors.length - visible.length
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900/50 dark:bg-red-950/20">
      <div className="mb-2 flex items-center gap-2">
        <AlertCircle className="h-5 w-5 text-red-500" />
        <h4 className="font-medium text-red-800 dark:text-red-300">
          {t('infrastructure.importRelationship.validationErrors', { count: errors.length })}
        </h4>
      </div>
      <ul className="space-y-1 text-sm text-red-700 dark:text-red-400">
        {visible.map((err, index) => (
          <li key={index}>
            {t('infrastructure.importRelationship.errorRow', {
              row: err.row,
              field: err.field,
              message: err.message,
            })}
          </li>
        ))}
        {hidden > 0 && (
          <li className="opacity-75">
            {t('infrastructure.importRelationship.andMoreErrors', { count: hidden })}
          </li>
        )}
      </ul>
    </div>
  )
}

export function DuplicateRelationshipsList({
  duplicates,
}: {
  duplicates: DuplicateRelationship[]
}): React.ReactElement {
  const { t } = useTranslation('cases')
  const visible = duplicates.slice(0, MAX_DUPLICATE_ROWS)
  const hidden = duplicates.length - visible.length
  return (
    <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
      <div className="mb-2 flex items-center gap-2">
        <FileText className="h-5 w-5 text-slate-500" />
        <h4 className="font-medium text-slate-700 dark:text-slate-300">
          {t('infrastructure.importRelationship.duplicateRelationships', {
            count: duplicates.length,
          })}
        </h4>
      </div>
      <p className="mb-2 text-sm text-slate-600 dark:text-slate-400">
        {t('infrastructure.importRelationship.duplicateRelationshipsDesc')}
      </p>
      <ul className="space-y-1 text-sm text-slate-600 dark:text-slate-400">
        {visible.map((dup) => (
          <li key={dup.row} className="font-mono text-xs">
            {dup.sourceHostname} → {dup.targetHostname} ({dup.connectionType})
          </li>
        ))}
        {hidden > 0 && (
          <li className="opacity-75">
            {t('infrastructure.importRelationship.andMore', { count: hidden })}
          </li>
        )}
      </ul>
    </div>
  )
}

export function RelationshipPreviewTable({
  rows,
}: {
  rows: RelationshipPreviewRow[]
}): React.ReactElement {
  const { t } = useTranslation('cases')
  const visible = rows.slice(0, MAX_PREVIEW_ROWS)
  return (
    <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
      <h4 className="mb-2 font-medium text-slate-700 dark:text-slate-300">
        {t('infrastructure.importRelationship.preview', { count: rows.length })}
      </h4>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 dark:border-slate-700">
              <th className="py-2 pr-4 text-left text-xs">
                {t('infrastructure.importRelationship.colRow')}
              </th>
              <th className="py-2 pr-4 text-left text-xs">
                {t('infrastructure.importRelationship.colSource')}
              </th>
              <th className="py-2 pr-4 text-left text-xs">
                {t('infrastructure.importRelationship.colTarget')}
              </th>
              <th className="py-2 pr-4 text-left text-xs">
                {t('infrastructure.importRelationship.colConnectionType')}
              </th>
              <th className="py-2 pr-4 text-left text-xs">
                {t('infrastructure.importRelationship.colStatus')}
              </th>
              <th className="py-2 text-left text-xs">
                {t('infrastructure.importRelationship.colLabel')}
              </th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <tr key={row.row} className="border-b border-slate-100 dark:border-slate-800">
                <td className="py-2 pr-4 text-slate-500 dark:text-slate-400">{row.row}</td>
                <td className="py-2 pr-4 font-mono text-xs">{row.sourceHostname}</td>
                <td className="py-2 pr-4 font-mono text-xs">{row.targetHostname}</td>
                <td className="py-2 pr-4">{row.connectionType}</td>
                <td className="py-2 pr-4">{row.status}</td>
                <td className="py-2">{row.label ?? '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

interface ValidationResultsProps {
  result: RelationshipImportValidationResult
  caseId: string
  onExecute: () => void | Promise<void>
  onCancel: () => void
  loading: boolean
}

export function RelationshipValidationResults({
  result,
  caseId,
  onExecute,
  onCancel,
  loading,
}: ValidationResultsProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const canImport = result.valid && result.validRecords > 0
  const handleExportUnresolved = useCallback(() => {
    void exportUnresolvedReferences(caseId, result.unresolvedReferences)
  }, [caseId, result.unresolvedReferences])
  return (
    <div className="space-y-6">
      <ValidationSummaryCard result={result} />
      {result.errors.length > 0 && <ValidationErrorsList errors={result.errors} />}
      {result.unresolvedReferences.length > 0 && (
        <UnresolvedReferencesPanel
          references={result.unresolvedReferences}
          onExport={handleExportUnresolved}
        />
      )}
      {result.duplicateRelationships.length > 0 && (
        <DuplicateRelationshipsList duplicates={result.duplicateRelationships} />
      )}
      {result.preview.length > 0 && <RelationshipPreviewTable rows={result.preview} />}
      <div className="flex justify-end gap-3 border-t border-slate-200 pt-4 dark:border-slate-700">
        <button
          onClick={onCancel}
          className="rounded-lg px-4 py-2 text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
        >
          {t('actions.cancel')}
        </button>
        {canImport && (
          <button
            onClick={() => void onExecute()}
            disabled={loading}
            className="flex items-center gap-2 rounded-lg bg-[#4e5e82] px-4 py-2 text-white hover:bg-[#5c6e94] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('infrastructure.importRelationship.importing')}
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                {t('infrastructure.importRelationship.importRelationships', {
                  count: result.validRecords,
                })}
              </>
            )}
          </button>
        )}
      </div>
    </div>
  )
}

export function RelationshipSuccessDisplay({
  result,
  onClose,
}: {
  result: RelationshipImportExecuteResult
  onClose: () => void
}): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="flex flex-col items-center justify-center py-8">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-950/50">
        <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
      </div>
      <h3 className="mt-4 text-lg font-semibold text-slate-900 dark:text-white">
        {t('infrastructure.importRelationship.importComplete')}
      </h3>
      <p className="mt-2 text-center text-slate-600 dark:text-slate-400">
        {t('infrastructure.importRelationship.successCount', { count: result.importedCount })}
      </p>
      <div className="mt-1 flex gap-4 text-sm text-slate-500 dark:text-slate-400">
        {result.skippedCount > 0 && (
          <span>
            {t('infrastructure.importRelationship.skippedCount', { count: result.skippedCount })}
          </span>
        )}
        {result.unresolvedCount > 0 && (
          <span>
            {t('infrastructure.importRelationship.unresolvedCount', {
              count: result.unresolvedCount,
            })}
          </span>
        )}
      </div>
      <button
        onClick={onClose}
        className="mt-6 rounded-lg bg-[#4e5e82] px-6 py-2 text-white hover:bg-[#5c6e94]"
      >
        {t('actions.done')}
      </button>
    </div>
  )
}

export function RelationshipErrorDisplay({
  error,
  onRetry,
  onClose,
}: {
  error: string
  onRetry: () => void
  onClose: () => void
}): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="flex flex-col items-center justify-center py-8">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-950/50">
        <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
      </div>
      <h3 className="mt-4 text-lg font-semibold text-slate-900 dark:text-white">
        {t('infrastructure.importRelationship.importFailed')}
      </h3>
      <p className="mt-2 text-center text-slate-600 dark:text-slate-400" role="alert">
        {error}
      </p>
      <div className="mt-6 flex gap-3">
        <button
          onClick={onClose}
          className="rounded-lg px-4 py-2 text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
        >
          {t('actions.cancel')}
        </button>
        <button
          onClick={onRetry}
          className="rounded-lg bg-[#4e5e82] px-4 py-2 text-white hover:bg-[#5c6e94]"
        >
          {t('actions.tryAgain')}
        </button>
      </div>
    </div>
  )
}

export function LoadingSpinner({ message }: { message: string }): React.ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12" role="status" aria-busy="true">
      <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
      <p className="mt-4 text-slate-600 dark:text-slate-400">{message}</p>
    </div>
  )
}
